import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail, Lock, Eye, EyeOff, ExternalLink,
  Gift, Wallet, History,
  TrendingUp, Zap, PieChart,
  Building2, Users, FileBarChart,
  MapPin, Percent, Network,
  ArrowRight, Store, CheckCircle, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, getRoleRedirect } from "@/contexts/AuthContext";
import { usePortal } from "@/contexts/PortalContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useBranding } from "@/contexts/BrandingContext";
import { getPortalForRole, PORTAL_LABELS, type PortalId } from "@/lib/portal";
import { setSessionIndicator, setSignedOutIndicator, clearSessionIndicator, getActivePortal, getIndicatorAccountUrl } from "@/lib/session-indicator";
import { isNativeApp } from "@/lib/native";

// ---------- Portal left-panel configs ----------
type FeatureItem = { icon: React.ElementType; title: string; desc: string };
type PanelConfig = { gradient: string; heading: string; sub: string; features: FeatureItem[] };

const PORTAL_PANEL: Record<string, PanelConfig> = {
  consumer: {
    gradient: "from-amber-500 via-orange-500 to-rose-600",
    heading: "Your Digital Voucher Wallet",
    sub: "Receive, redeem, and manage your vouchers in one place.",
    features: [
      { icon: Gift,    title: "Receive & Gift",    desc: "Accept vouchers from brands, friends, and family" },
      { icon: Wallet,  title: "Redeem Anywhere",   desc: "Use your vouchers at thousands of merchant locations" },
      { icon: History, title: "Track Your Balance", desc: "Full spending history and remaining-value at a glance" },
    ],
  },
  merchant: {
    gradient: "from-emerald-600 via-teal-700 to-green-900",
    heading: "Accept Vouchers. Grow Revenue.",
    sub: "Seamless voucher redemptions and real-time settlement for your business.",
    features: [
      { icon: TrendingUp, title: "Instant Settlement",  desc: "Payments land in your account the moment a voucher is redeemed" },
      { icon: Zap,        title: "Fast Redemption",     desc: "Scan or enter a code — verified and settled in seconds" },
      { icon: PieChart,   title: "Business Insights",   desc: "Redemption trends, revenue analytics, and customer data" },
    ],
  },
  corporate: {
    gradient: "from-blue-600 via-indigo-700 to-blue-950",
    heading: "Empower Your Workforce",
    sub: "Issue and manage vouchers for employees, customers, and partners.",
    features: [
      { icon: Building2,    title: "Bulk Issuance",          desc: "Distribute thousands of vouchers to your team instantly" },
      { icon: Users,        title: "Employee Benefits",       desc: "Manage allocations and policies across departments" },
      { icon: FileBarChart, title: "Reporting & Compliance", desc: "Detailed spend reports and audit-ready records" },
    ],
  },
  agent: {
    gradient: "from-purple-600 via-violet-700 to-purple-950",
    heading: "Sell Vouchers. Earn More.",
    sub: "Grow your territory and track every commission in real time.",
    features: [
      { icon: MapPin,  title: "Territory Management", desc: "Oversee your sales area and merchant client accounts" },
      { icon: Percent, title: "Commission Tracking",  desc: "Live view of earnings, bonuses, and payout history" },
      { icon: Network, title: "Network Growth",       desc: "Onboard new merchants and expand your footprint" },
    ],
  },
};

const DEFAULT_PANEL: PanelConfig = {
  gradient: "from-brand-forest via-brand-green-dk to-brand-green",
  heading: "Welcome Back",
  sub: "Access your portal to manage vouchers, track redemptions, and grow your business.",
  features: [],
};

// ---------- Shared wrong-portal banner ----------
const WrongPortalBanner = ({ wrongPortal }: { wrongPortal: { label: string; url: string } }) => (
  <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4">
    <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
      This account belongs to the <strong>{wrongPortal.label}</strong>.
    </p>
    <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
      Please sign in at the correct portal for your account type.
    </p>
    <a
      href={wrongPortal.url}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-200 underline underline-offset-2 hover:text-amber-900"
    >
      Go to {wrongPortal.label} <ExternalLink className="w-3 h-3" />
    </a>
  </div>
);

// ---------- Shared form fields ----------
const LoginForm = ({
  email, setEmail, password, setPassword,
  showPassword, setShowPassword,
  loading, handleSubmit, buttonClass,
}: {
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  showPassword: boolean; setShowPassword: (v: boolean) => void;
  loading: boolean; handleSubmit: (e: React.FormEvent) => void;
  buttonClass: string;
}) => (
  <form onSubmit={handleSubmit} className="space-y-5">
    <div className="space-y-2">
      <Label htmlFor="email" className="text-sm font-medium text-foreground">Email Address</Label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          id="email" type="email" placeholder="you@company.com"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="pl-10" required
        />
      </div>
    </div>
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
        <Link to="/reset-password" className="text-sm text-brand-green-dk hover:text-brand-forest transition-colors">
          Forgot password?
        </Link>
      </div>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          id="password" type={showPassword ? "text" : "password"} placeholder="••••••••••"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="pl-10 pr-10" required minLength={10}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
    <Button type="submit" disabled={loading} className={`w-full h-12 font-semibold ${buttonClass}`}>
      {loading ? "Signing in..." : "Log In"}
    </Button>
  </form>
);

// ---------- Portal left panel ----------
const PortalLeftPanel = ({ config, iconLogo }: { config: PanelConfig; iconLogo: string }) => (
  <div className={`hidden lg:flex lg:w-1/2 bg-gradient-to-br ${config.gradient} items-center justify-center p-12`}>
    <div className="max-w-sm w-full">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <img
          src={iconLogo}
          alt="VGV"
          className="h-20 w-20 mx-auto mb-8 object-contain drop-shadow-lg"
        />
        <h1 className="text-3xl font-bold text-white mb-3 text-center">{config.heading}</h1>
        <p className="text-white/70 text-center mb-8 text-sm leading-relaxed">{config.sub}</p>
        {config.features.length > 0 && (
          <div className="space-y-4">
            {config.features.map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{f.title}</p>
                  <p className="text-white/65 text-xs mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  </div>
);

// ---------- Main component ----------
const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [wrongPortal, setWrongPortal] = useState<{ portalId: PortalId; label: string; url: string } | null>(null);
  const { signIn, user, profile, loading: authLoading, profileError } = useAuth();
  const { portal, label: portalLabel, allowedRoles, isPortalMode, getPortalUrl } = usePortal();
  const { toast } = useToast();
  const { iconLogo, fullLogo } = useBranding();

  // Cross-subdomain session indicator — tells us if another portal is already logged in
  const indicatorPortal = getActivePortal();

  // Clear the cross-portal indicator so the chooser shows again. Other portal
  // tabs will self-evict the moment they regain focus (ProtectedRoute polls
  // the indicator). The local Supabase session on this domain (if any) is
  // already gone or irrelevant since we're showing the chooser.
  const handleSignInDifferent = async () => {
    // .catch: the auth-token Web Lock can be stolen by a concurrent refresh
    // (supabase-js race) — indicator cleanup and reload must still run.
    await supabase.auth.signOut().catch(() => {});
    setSignedOutIndicator();
    window.location.reload();
  };

  const Spinner = () => (
    <div className="h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (authLoading) return <Spinner />;

  if (user) {
    if (profile) {
      const portalId = getPortalForRole(profile.role);
      if (portalId) {
        if (isPortalMode && portalId !== portal) {
          // Wrong-portal session — show the banner instead of auto-redirecting.
          // Guard with !wrongPortal to avoid re-render loop: setWrongPortal causes
          // a re-render while user is still set (signOut is async), so without the
          // guard this block would fire again on every render until user clears.
          if (!wrongPortal) {
            const correctUrl = getPortalUrl(portalId, "/login");
            setWrongPortal({ portalId, label: PORTAL_LABELS[portalId], url: correctUrl });
            supabase.auth.signOut().catch(() => {}).then(() => clearSessionIndicator());
          }
        } else {
          // Do NOT call setSessionIndicator here — this path runs on every session
          // restore (page refresh, new tab). Writing the indicator here would
          // overwrite whichever portal legitimately owns the cookie, defeating
          // cross-portal eviction. The indicator is written only in handleSubmit
          // (fresh login) and the ProtectedRoute re-claim effect (null-cookie recovery).
          window.location.href = getPortalUrl(portalId, getRoleRedirect(profile.role));
        }
      }
      // Keep the spinner on screen while sign-out completes (avoids blank flash)
      return <Spinner />;
    }
    if (profileError) {
      return (
        <div className="h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm font-medium text-slate-700">Unable to load your account. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} className="text-sm text-brand-forest underline">Refresh</button>
        </div>
      );
    }
    // User set but profile still pending (edge case) — keep spinning
    return <Spinner />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setWrongPortal(null);

    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      toast({ title: "Sign in failed", description: error.message, variant: "destructive" });
      return;
    }

    const MAX_POLL_ATTEMPTS = 20;
    const POLL_INTERVAL_MS = 300;

    const checkProfile = async (attempts = 0) => {
      if (attempts > MAX_POLL_ATTEMPTS) { window.location.href = "/"; return; }

      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return; // user signed out during polling — stop

      const { data } = await supabase
        .from("profiles").select("role").eq("id", currentUser.id).single();

      if (!data?.role) { setTimeout(() => checkProfile(attempts + 1), POLL_INTERVAL_MS); return; }

      const role = data.role as string;

      if (isPortalMode && allowedRoles.length > 0 && !allowedRoles.includes(role)) {
        await supabase.auth.signOut().catch(() => {});
        clearSessionIndicator();
        const correctPortalId = getPortalForRole(role);
        if (correctPortalId) {
          const correctUrl = getPortalUrl(correctPortalId, "/login");
          const { PORTAL_LABELS } = await import("@/lib/portal");
          setWrongPortal({ portalId: correctPortalId, label: PORTAL_LABELS[correctPortalId], url: correctUrl });
        } else {
          toast({
            title: "Wrong portal",
            description: "This account type is not supported on this portal. Please check the URL.",
            variant: "destructive",
          });
        }
        return;
      }

      const portalId = getPortalForRole(role);
      if (portalId) setSessionIndicator(portalId);

      const dashboardUrl = portalId ? getPortalUrl(portalId, getRoleRedirect(role)) : getRoleRedirect(role);

      // Single-portal singleton is enforced reactively: the indicator cookie
      // (set above) is the source of truth, and every portal's ProtectedRoute
      // polls it once a second + on focus/visibility, evicting itself on
      // mismatch. Any other portal tab the user has open will sign itself
      // out the moment they look at it. No chain redirects needed.
      window.location.href = dashboardUrl;
    };

    checkProfile();
  };

  // ── Main domain (no portal) — show login type chooser ──────────────────────
  if (!isPortalMode) {
    const primaryOptions = [
      {
        href: "https://app.valuegiftvouchers.com/login",
        label: "Personal Account",
        sub: "Receive, redeem & gift vouchers as an individual",
        icon: Wallet,
        gradient: "from-amber-400 to-orange-500",
        hoverBorder: "hover:border-amber-400",
        hoverArrow: "group-hover:text-amber-500",
      },
      {
        href: "https://corporate.valuegiftvouchers.com/login",
        label: "Corporate Account",
        sub: "Issue & manage vouchers for your organisation",
        icon: Building2,
        gradient: "from-blue-500 to-indigo-600",
        hoverBorder: "hover:border-blue-400",
        hoverArrow: "group-hover:text-blue-500",
      },
    ];

    const secondaryOptions = [
      {
        href: "https://merchant.valuegiftvouchers.com/login",
        label: "Merchant",
        icon: Store,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        hover: "hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30",
      },
      {
        href: "https://agent.valuegiftvouchers.com/login",
        label: "Agent",
        icon: Users,
        iconBg: "bg-purple-100 dark:bg-purple-900/40",
        iconColor: "text-purple-600 dark:text-purple-400",
        hover: "hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30",
      },
    ];

    // ── Already signed in: force full logout chain before showing the chooser ──
    if (indicatorPortal) {
      return (
        <div className="min-h-screen flex">
          <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 relative bg-gradient-to-br from-brand-forest via-brand-green-dk to-brand-green overflow-hidden flex-col items-center justify-center p-12">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/5 rounded-full" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="relative z-10 max-w-xs w-full text-center">
              <img src={iconLogo} alt="VGV" className="w-20 h-20 mx-auto mb-8 object-contain drop-shadow-lg" />
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">Ghana's Digital<br />Voucher Platform</h1>
            </motion.div>
          </div>
          <div className="w-full lg:w-7/12 xl:w-3/5 flex flex-col items-center justify-center px-6 py-12 sm:px-12 bg-background">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md text-center">
              <div className="lg:hidden mb-8">
                <Link to="/"><img src={fullLogo} alt="Value Gift Vouchers" className="h-64 mx-auto object-contain" /></Link>
              </div>
              <div className="w-16 h-16 rounded-2xl bg-brand-forest/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-brand-forest" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Already signed in</h2>
              <p className="text-muted-foreground mb-8">
                You're currently signed in to{" "}
                <strong className="text-foreground">{PORTAL_LABELS[indicatorPortal]}</strong>.
              </p>
              <div className="space-y-3">
                <a href={getIndicatorAccountUrl(indicatorPortal)} className="block">
                  <Button className="w-full h-12 bg-brand-forest hover:bg-brand-green-dk text-white font-semibold">
                    Go to Account
                  </Button>
                </a>
                <Button variant="outline" className="w-full h-12" onClick={handleSignInDifferent}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign in to a different account
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-5">
                Signing in to a different account will automatically log you out of{" "}
                {PORTAL_LABELS[indicatorPortal]}.
              </p>
              <div className="mt-5">
                <Link to="/" className="text-sm text-muted-foreground hover:text-brand-forest transition-colors">← Back to Home</Link>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex">
        {/* ── Left brand panel (desktop only) ── */}
        <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 relative bg-gradient-to-br from-brand-forest via-brand-green-dk to-brand-green overflow-hidden flex-col items-center justify-center p-12">
          {/* Decorative circles */}
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/5 rounded-full" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 right-6 w-24 h-24 bg-white/5 rounded-full" />

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
            className="relative z-10 max-w-xs w-full"
          >
            <img
              src={iconLogo}
              alt="VGV"
              className="w-20 h-20 mx-auto mb-8 object-contain drop-shadow-lg"
            />
            <h1 className="text-3xl font-bold text-white mb-3 text-center leading-tight">
              Ghana's Digital<br />Voucher Platform
            </h1>
            <p className="text-white/65 text-sm text-center mb-10 leading-relaxed">
              Trusted by thousands of individuals, businesses, and merchants across Ghana.
            </p>

            <div className="space-y-4">
              {[
                { icon: Gift,       text: "Buy, gift & redeem vouchers instantly" },
                { icon: Zap,        text: "1,000+ merchant acceptance points" },
                { icon: TrendingUp, text: "Powering corporate benefits programmes" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white/80 text-sm">{text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Right chooser panel ── */}
        <div className="w-full lg:w-7/12 xl:w-3/5 flex flex-col items-center justify-center px-6 py-12 sm:px-12 bg-background">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="w-full max-w-md"
          >
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/">
                <img src={fullLogo} alt="Value Gift Vouchers" className="h-64 mx-auto object-contain" />
              </Link>
            </div>

            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-foreground">Sign in to VGV</h2>
              <p className="text-muted-foreground mt-1.5 text-sm">
                Select the account type that matches your profile.
              </p>
            </div>

            {/* Primary options */}
            <div className="space-y-3 mb-6">
              {primaryOptions.map(({ href, label, sub, icon: Icon, gradient, hoverBorder, hoverArrow }, i) => (
                <motion.a
                  key={href}
                  href={href}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.15 + i * 0.08 }}
                  whileHover={{ x: 3 }}
                  className={`group flex items-center gap-4 rounded-2xl border-2 border-border bg-card p-5 ${hoverBorder} hover:shadow-md transition-all duration-200 cursor-pointer`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{sub}</p>
                  </div>
                  <ArrowRight className={`w-5 h-5 text-muted-foreground ${hoverArrow} group-hover:translate-x-1 transition-all flex-shrink-0`} />
                </motion.a>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground px-1">Other portals</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Secondary portals */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="grid grid-cols-2 gap-3 mb-8"
            >
              {secondaryOptions.map(({ href, label, icon: Icon, iconBg, iconColor, hover }) => (
                <a
                  key={href}
                  href={href}
                  className={`flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 ${hover} transition-all duration-200`}
                >
                  <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                  </div>
                  <span className="text-sm font-medium text-foreground">{label}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
                </a>
              ))}
            </motion.div>

            {/* Admin footnote */}
            <p className="text-xs text-muted-foreground text-center">
              Platform administration?{" "}
              <a
                href="https://admin.valuegiftvouchers.com/login"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                admin.valuegiftvouchers.com
              </a>
            </p>

            <div className="mt-5 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-brand-forest transition-colors">
                ← Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Admin portal ────────────────────────────────────────────────────────────
  if (portal === "admin") {
    if (indicatorPortal && indicatorPortal !== "admin") {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-brand-green" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Already signed in</h1>
            <p className="text-slate-400 mb-8">
              You're currently signed in to{" "}
              <strong className="text-white">{PORTAL_LABELS[indicatorPortal]}</strong>.
            </p>
            <div className="space-y-3">
              <a href={getIndicatorAccountUrl(indicatorPortal)} className="block">
                <Button className="w-full h-12 bg-brand-green hover:bg-brand-green-dk text-white font-semibold">
                  Go to Account
                </Button>
              </a>
              <Button
                variant="outline"
                className="w-full h-12 border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={handleSignInDifferent}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign in to a different account
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-5">
              Signing in to a different account will log you out of {PORTAL_LABELS[indicatorPortal]}.
            </p>
          </motion.div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-24 h-24 rounded-2xl bg-slate-800 flex items-center justify-center mb-6 overflow-hidden p-3">
              <img src={iconLogo} alt="VGV" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1 text-center">Platform Administration</h1>
            <p className="text-slate-400 text-sm text-center">Authorized personnel only</p>
          </div>


          {wrongPortal && (
            <div className="mb-6 rounded-lg border border-amber-800 bg-amber-950 p-4">
              <p className="text-sm font-medium text-amber-100 mb-1">
                This account belongs to the <strong>{wrongPortal.label}</strong>.
              </p>
              <p className="text-xs text-amber-300 mb-3">
                Please sign in at the correct portal for your account type.
              </p>
              <a
                href={wrongPortal.url}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 underline underline-offset-2 hover:text-amber-100"
              >
                Go to {wrongPortal.label} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-300">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="email" type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-brand-green"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-300">Password</Label>
                  <Link to="/reset-password" className="text-sm text-brand-green hover:text-brand-green-dk transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password" type={showPassword ? "text" : "password"} placeholder="••••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-brand-green"
                    required minLength={10}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dk text-white h-12 font-semibold"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://valuegiftvouchers.com"
              className="text-sm text-slate-400 hover:text-slate-300 transition-colors"
            >
              ← Back to main site
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Portal & main-site two-panel layout ─────────────────────────────────────
  const panelConfig = portal ? (PORTAL_PANEL[portal] ?? DEFAULT_PANEL) : DEFAULT_PANEL;
  const formTitle = isPortalMode ? `Sign in to ${portalLabel}` : "Sign In";
  const formSubtitle = isPortalMode
    ? `Enter your credentials to access the ${portalLabel.toLowerCase()}`
    : "Enter your credentials to access your portal";

  // Block login on portal pages if another portal is already active.
  // The form is never shown until the user explicitly switches accounts,
  // which sets the signed_out sentinel and triggers eviction on the old portal.
  if (indicatorPortal && portal && indicatorPortal !== portal) {
    return (
      <div className="min-h-screen bg-background flex">
        <PortalLeftPanel config={panelConfig} iconLogo={iconLogo} />
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md text-center"
          >
            <div className="lg:hidden mb-8">
              <Link to="/"><img src={fullLogo} alt="Value Gift Vouchers" className="h-64 mx-auto object-contain" /></Link>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-brand-forest/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-brand-forest" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Already signed in</h2>
            <p className="text-muted-foreground mb-8">
              You're currently signed in to{" "}
              <strong className="text-foreground">{PORTAL_LABELS[indicatorPortal]}</strong>.
            </p>
            <div className="space-y-3">
              <a href={getIndicatorAccountUrl(indicatorPortal)} className="block">
                <Button className="w-full h-12 bg-brand-forest hover:bg-brand-green-dk text-white font-semibold">
                  Go to Account
                </Button>
              </a>
              <Button variant="outline" className="w-full h-12" onClick={handleSignInDifferent}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign in to a different account
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-5">
              Signing in to a different account will automatically log you out of{" "}
              {PORTAL_LABELS[indicatorPortal]}.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel — unique per portal */}
      <PortalLeftPanel config={panelConfig} iconLogo={iconLogo} />

      {/* Right Panel — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/">
              <img src={fullLogo} alt="Value Gift Vouchers" className="h-64 mx-auto object-contain" />
            </Link>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-foreground">{formTitle}</h2>
            <p className="text-muted-foreground mt-1">{formSubtitle}</p>
          </div>

          {wrongPortal && <WrongPortalBanner wrongPortal={wrongPortal} />}

          <LoginForm
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            showPassword={showPassword} setShowPassword={setShowPassword}
            loading={loading} handleSubmit={handleSubmit}
            buttonClass="bg-brand-forest hover:bg-brand-green-dk text-primary-foreground"
          />

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/register" className="text-brand-green-dk hover:text-brand-forest font-medium transition-colors">
                Create Account
              </Link>
            </p>
          </div>

          {!isNativeApp() && (
            <div className="mt-4 text-center">
              <a
                href="https://valuegiftvouchers.com"
                className="text-sm text-muted-foreground hover:text-brand-forest transition-colors"
              >
                ← Back to Home
              </a>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
